import type { PaymentMethodId } from "@/components/booking/PaymentStep";
import type { ServiceInfo } from "@/components/booking/types";

export const BOOKING_DRAFT_TTL_MS = 30 * 60 * 1000;

export interface BookingDraft {
  therapistId: string;
  serviceId: string;
  date: string;
  time: string;
  paymentMethod: PaymentMethodId;
  modality: "online" | "in_person";
  savedAt: number;
  /** Ostali članovi; stariji draftovi ga nemaju. */
  memberEmails?: string[];
}

export interface RestoredBookingDraft {
  service: ServiceInfo;
  date: Date;
  time: string;
  paymentMethod: PaymentMethodId;
  modality: "online" | "in_person";
  memberEmails: string[];
}

export function bookingDraftKey(therapistId: string): string {
  return `psihotron:booking-draft:${therapistId}`;
}

export function buildBookingDraft(input: BookingDraft): BookingDraft {
  return input;
}

function parseLocalDate(date: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

export function restoreBookingDraft(
  draft: BookingDraft | null,
  services: ServiceInfo[],
  therapistId: string,
  now = Date.now(),
): RestoredBookingDraft | null {
  if (!draft || draft.therapistId !== therapistId) return null;
  if (now - draft.savedAt > BOOKING_DRAFT_TTL_MS) return null;

  const service = services.find((item) => item.id === draft.serviceId);
  const date = parseLocalDate(draft.date);
  if (!service || !date) return null;

  return {
    service,
    date,
    time: draft.time,
    paymentMethod: draft.paymentMethod,
    modality: draft.modality,
    memberEmails: Array.isArray(draft.memberEmails) ? draft.memberEmails : [],
  };
}

export function readBookingDraft(storage: Storage, therapistId: string): BookingDraft | null {
  const raw = storage.getItem(bookingDraftKey(therapistId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as BookingDraft;
  } catch {
    return null;
  }
}

export function writeBookingDraft(storage: Storage, draft: BookingDraft): void {
  storage.setItem(bookingDraftKey(draft.therapistId), JSON.stringify(draft));
}

export function clearBookingDraft(storage: Storage, therapistId: string): void {
  storage.removeItem(bookingDraftKey(therapistId));
}
